import { useMediaQuery, useTheme } from "@mui/material";
import { ModuleRegistry } from "ag-grid-community";
import { AllEnterpriseModule, LicenseManager } from "ag-grid-enterprise";
import { Roles } from "data/@types/Amplify";
import { DOCUMENT_TYPE_CODES } from "data/@types/due-diligence/DocumentType";
import { AccessLevel } from "data/@types/Utils";
import React, { lazy, Suspense } from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from "react-router-dom";
import ProtectedRoute from "ui/components/route/ProtectedRoute";
import ActivityDetailsPageSkeleton from "ui/components/surfaces/skeleton/ActivityDetailsPageSkeleton";
import DueDiligencePageSkeleton from "ui/components/surfaces/skeleton/due-diligence/DueDiligencePageSkeleton";
import EntityDetailsPageSkeleton from "ui/components/surfaces/skeleton/EntityDetailsPageSkeleton";
import HomeDashboardSkeleton from "ui/components/surfaces/skeleton/HomeDashboardSkeleton";
import { ValidateRoute } from "utils/route/ValidateRoutePath";
import { withRouteKey } from "utils/route/withRouteKey";
import { MAF_CREATE_ROLES } from "utils/mafAuth";
import App from "./App";

const agGridLicenseKey = import.meta.env.VITE_AG_GRID_LICENSE_KEY;
if (!agGridLicenseKey && import.meta.env.MODE !== "development") {
  console.error(
    "VITE_AG_GRID_LICENSE_KEY is not set — ag-grid watermark will appear",
  );
}
LicenseManager.setLicenseKey(agGridLicenseKey ?? "");
ModuleRegistry.registerModules([AllEnterpriseModule]);

const HomeDashboard = lazy(() => import("ui/pages/HomeDashboard"));
const HomeDashboardMobile = lazy(() => import("ui/pages/HomeDashboardMobile"));
const FirmDetailsPage = withRouteKey(
  lazy(() => import("ui/pages/FirmDetails")),
  "coreId",
);
const ProductDetailsPage = withRouteKey(
  lazy(() => import("ui/pages/ProductDetails")),
  "coreId",
);
const StrategyDetailsPage = withRouteKey(
  lazy(() => import("ui/pages/StrategyDetails")),
  "strategyId",
);
const CommentDetailsEditable = React.lazy(() =>
  import("ui/pages/CommentDetailsEditable").then((module) => ({
    default: module.CommentDetailsEditable,
  })),
);
const CommentCreation = React.lazy(() =>
  import("ui/pages/CommentDetailsEditable").then((module) => ({
    default: module.CommentCreation,
  })),
);
const CommentDetailsReadOnlyPage = lazy(
  () => import("ui/pages/CommentDetailsReadOnlyPage"),
);
const MeetingDetailsPage = lazy(() => import("ui/pages/MeetingDetails"));
const AnnualMeetingNoteCreation = React.lazy(() =>
  import("ui/pages/AnnualMeetingNote").then((module) => ({
    default: module.AnnualMeetingNoteCreation,
  })),
);
const AnnualMeetingNoteReadOnly = lazy(
  () => import("ui/pages/AnnualMeetingNoteReadOnly"),
);
const AnnualMeetingNoteEditable = lazy(
  () => import("ui/pages/AnnualMeetingNoteEditable"),
);

const DueDiligencePage = lazy(() => import("ui/pages/DueDiligencePage"));
const MafCreation = lazy(() => import("ui/pages/MafCreation"));

const BulkMoveAuditPage = lazy(() => import("ui/pages/BulkMoveAuditPage"));
const BulkMoveNewPage = lazy(() => import("ui/pages/BulkMoveNewPage"));
const SiiFundLabelsPage = lazy(() => import("ui/pages/SiiFundLabelsPage"));
const BulkMoveAuditDetailPage = withRouteKey(
  lazy(() => import("ui/pages/BulkMoveAuditDetailPage")),
  "bulkMoveId",
);

const DEVTOOLS_ENABLED = import.meta.env.VITE_ENABLE_DEVTOOLS === "true";

// PURE annotations let Rollup drop these (and their chunks) when the
// devtoolsRoutes branch below tree-shakes away in non-devtools builds.
const DevtoolsPage = /*#__PURE__*/ lazy(
  () => import("ui/pages/devtools/DevtoolsPage"),
);
const RmdDevtoolsPage = /*#__PURE__*/ lazy(
  () => import("ui/pages/devtools/rmd/RmdDevtoolsPage"),
);
const SkillViewerPage = /*#__PURE__*/ lazy(
  () => import("ui/pages/devtools/rmd/SkillViewerPage"),
);

// Top-level constant (not an in-component conditional) so Rollup can fold
// DEVTOOLS_ENABLED at build time and eliminate the route elements — and with
// them the lazy imports — from non-devtools builds.
const devtoolsRoutes = DEVTOOLS_ENABLED ? (
  <>
    <Route
      path="devtools"
      element={
        <Suspense fallback={null}>
          <DevtoolsPage />
        </Suspense>
      }
    />
    <Route
      path="devtools/rmd"
      element={
        <Suspense fallback={null}>
          <RmdDevtoolsPage />
        </Suspense>
      }
    />
    <Route
      path="devtools/rmd/skill-viewer"
      element={
        <Suspense fallback={null}>
          <SkillViewerPage />
        </Suspense>
      }
    />
    <Route
      path="devtools/rmd/charlesbank11-test"
      element={
        <Navigate to="/strategy-details/1504/fund/63334/idd/test" replace />
      }
    />
  </>
) : null;

const MainRouter = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Router>
      <Routes>
        <Route path="/" element={<App />}>
          {/* Home */}
          <Route index element={<Navigate to="/home" replace />} />
          <Route
            path="home"
            element={
              <Suspense fallback={<HomeDashboardSkeleton />}>
                {isMobile ? <HomeDashboardMobile /> : <HomeDashboard />}
              </Suspense>
            }
          />

          {/* New */}
          <Route
            element={
              <ProtectedRoute
                allowedRoles={[
                  Roles.ADMIN,
                  Roles.GIR_WRITER,
                  Roles.GIS_WRITER,
                  Roles.GOV_ADMIN,
                ]}
              />
            }
          >
            <Route
              path="comment/new"
              element={
                <ValidateRoute
                  type="enum"
                  enumType={AccessLevel}
                  paramName="type"
                >
                  <Suspense fallback={<ActivityDetailsPageSkeleton />}>
                    <CommentCreation />
                  </Suspense>
                </ValidateRoute>
              }
            />
            <Route
              path="meeting/new"
              element={
                <Suspense fallback={<ActivityDetailsPageSkeleton />}>
                  <MeetingDetailsPage />
                </Suspense>
              }
            />
            <Route
              path="annual-meeting-note/new"
              element={
                <Suspense fallback={<ActivityDetailsPageSkeleton />}>
                  <AnnualMeetingNoteCreation />
                </Suspense>
              }
            />
            <Route
              path="product-details/:coreId/new"
              element={
                <ValidateRoute type="uuid" paramName="coreId">
                  <Suspense fallback={<EntityDetailsPageSkeleton />}>
                    <ProductDetailsPage />
                  </Suspense>
                </ValidateRoute>
              }
            />
          </Route>

          <Route element={<ProtectedRoute allowedRoles={MAF_CREATE_ROLES} />}>
            <Route
              path="maf/new"
              element={
                <Suspense fallback={<ActivityDetailsPageSkeleton />}>
                  <MafCreation />
                </Suspense>
              }
            />
          </Route>

          {/* Edit */}
          <Route
            element={
              <ProtectedRoute
                allowedRoles={[Roles.ADMIN, Roles.GIR_WRITER, Roles.GIS_WRITER]}
              />
            }
          >
            <Route
              path="comment/:commentId/edit"
              element={
                <ValidateRoute type="number" paramName="commentId">
                  <Suspense fallback={<ActivityDetailsPageSkeleton />}>
                    <CommentDetailsEditable />
                  </Suspense>
                </ValidateRoute>
              }
            />
            <Route
              path="meeting/:meetingId/edit"
              element={
                <ValidateRoute type="number" paramName="meetingId">
                  <Suspense fallback={<ActivityDetailsPageSkeleton />}>
                    <MeetingDetailsPage />
                  </Suspense>
                </ValidateRoute>
              }
            />
            <Route
              path="product-details/:coreId/edit"
              element={
                <ValidateRoute type="uuid" paramName="coreId">
                  <Suspense fallback={<EntityDetailsPageSkeleton />}>
                    <ProductDetailsPage />
                  </Suspense>
                </ValidateRoute>
              }
            />
            <Route
              path="annual-meeting-note/:commentId/edit"
              element={
                <ValidateRoute type="number" paramName="commentId">
                  <Suspense fallback={<ActivityDetailsPageSkeleton />}>
                    <AnnualMeetingNoteEditable />
                  </Suspense>
                </ValidateRoute>
              }
            />
          </Route>

          {/* Details */}
          <Route
            element={
              <ProtectedRoute
                allowedRoles={[
                  Roles.ADMIN,
                  Roles.READER,
                  Roles.GIR_WRITER,
                  Roles.GIS_WRITER,
                ]}
              />
            }
          >
            <Route
              path="firm-details/:coreId"
              element={
                <ValidateRoute type="uuid" paramName="coreId">
                  <Suspense fallback={<EntityDetailsPageSkeleton />}>
                    <FirmDetailsPage />
                  </Suspense>
                </ValidateRoute>
              }
            />
            <Route
              path="product-details/:coreId"
              element={
                <ValidateRoute type="uuid" paramName="coreId">
                  <Suspense fallback={<EntityDetailsPageSkeleton />}>
                    <ProductDetailsPage />
                  </Suspense>
                </ValidateRoute>
              }
            />
            <Route
              path="comment/:commentId"
              element={
                <ValidateRoute type="number" paramName="commentId">
                  <Suspense fallback={<ActivityDetailsPageSkeleton />}>
                    <CommentDetailsReadOnlyPage />
                  </Suspense>
                </ValidateRoute>
              }
            />
            <Route
              path="meeting/:meetingId"
              element={
                <ValidateRoute type="number" paramName="meetingId">
                  <Suspense fallback={<ActivityDetailsPageSkeleton />}>
                    <MeetingDetailsPage />
                  </Suspense>
                </ValidateRoute>
              }
            />
            <Route
              path="annual-meeting-note/:commentId"
              element={
                <ValidateRoute type="number" paramName="commentId">
                  <Suspense fallback={<ActivityDetailsPageSkeleton />}>
                    <AnnualMeetingNoteReadOnly />
                  </Suspense>
                </ValidateRoute>
              }
            />
          </Route>

          {/* Strategy */}
          <Route path="strategy-details">
            <Route path=":strategyId">
              {/* Read */}
              <Route
                element={
                  <ProtectedRoute
                    allowedRoles={[
                      Roles.ADMIN,
                      Roles.READER,
                      Roles.GIR_WRITER,
                      Roles.GIS_WRITER,
                      Roles.GOV_ADMIN,
                    ]}
                  />
                }
              >
                <Route
                  index
                  element={
                    <ValidateRoute type="number" paramName="strategyId">
                      <Suspense
                        fallback={<EntityDetailsPageSkeleton withFunds />}
                      >
                        <StrategyDetailsPage />
                      </Suspense>
                    </ValidateRoute>
                  }
                />
                <Route path="fund">
                  <Route path=":fundId">
                    <Route
                      path=":documentType"
                      element={
                        <ValidateRoute
                          type="enum"
                          paramName="documentType"
                          enumType={DOCUMENT_TYPE_CODES}
                        >
                          <ValidateRoute type="number" paramName="fundId">
                            <Suspense fallback={<DueDiligencePageSkeleton />}>
                              <DueDiligencePage />
                            </Suspense>
                          </ValidateRoute>
                        </ValidateRoute>
                      }
                    >
                      <Route path="test" />
                      <Route
                        path=":documentId"
                        element={
                          <ValidateRoute type="number" paramName="documentId">
                            <Suspense fallback={<DueDiligencePageSkeleton />}>
                              <DueDiligencePage />
                            </Suspense>
                          </ValidateRoute>
                        }
                      >
                        <Route path="test" />
                      </Route>
                    </Route>
                  </Route>
                </Route>
              </Route>

              {/* Edit */}
              <Route
                element={
                  <ProtectedRoute
                    allowedRoles={[
                      Roles.ADMIN,
                      Roles.GIR_WRITER,
                      Roles.GIS_WRITER,
                    ]}
                  />
                }
              >
                <Route path="edit">
                  <Route
                    index
                    element={
                      <ValidateRoute type="number" paramName="strategyId">
                        <Suspense
                          fallback={<EntityDetailsPageSkeleton withFunds />}
                        >
                          <StrategyDetailsPage />
                        </Suspense>
                      </ValidateRoute>
                    }
                  />
                </Route>
              </Route>

              {/* New */}
              <Route
                element={
                  <ProtectedRoute
                    allowedRoles={[
                      Roles.ADMIN,
                      Roles.GIR_WRITER,
                      Roles.GIS_WRITER,
                      Roles.GOV_ADMIN,
                    ]}
                  />
                }
              >
                <Route path="new">
                  <Route
                    index
                    element={
                      <ValidateRoute type="number" paramName="strategyId">
                        <Suspense
                          fallback={<EntityDetailsPageSkeleton withFunds />}
                        >
                          <StrategyDetailsPage />
                        </Suspense>
                      </ValidateRoute>
                    }
                  />
                </Route>
              </Route>
            </Route>
          </Route>

          <Route element={<ProtectedRoute allowedRoles={[Roles.ADMIN]} />}>
            <Route
              path="bulk-move"
              element={
                <Suspense fallback={null}>
                  <BulkMoveAuditPage />
                </Suspense>
              }
            />
            <Route
              path="bulk-move/new"
              element={
                <Suspense fallback={null}>
                  <BulkMoveNewPage />
                </Suspense>
              }
            />
            <Route
              path="bulk-move/:bulkMoveId"
              element={
                <ValidateRoute type="uuid" paramName="bulkMoveId">
                  <Suspense fallback={null}>
                    <BulkMoveAuditDetailPage />
                  </Suspense>
                </ValidateRoute>
              }
            />
          </Route>

          <Route
            element={
              <ProtectedRoute
                allowedRoles={[
                  Roles.ADMIN,
                  Roles.READER,
                  Roles.GIR_WRITER,
                  Roles.GIS_WRITER,
                  Roles.GOV_ADMIN,
                ]}
              />
            }
          >
            <Route
              path="sii-fund-labels"
              element={
                <Suspense fallback={<EntityDetailsPageSkeleton />}>
                  <SiiFundLabelsPage />
                </Suspense>
              }
            />
          </Route>
          {devtoolsRoutes}
        </Route>
      </Routes>
    </Router>
  );
};

export default MainRouter;
